import { amplitudeTracker } from '../amplitude-tracker';
import config from '../../../config';

const DEFAULT_ERROR = {
    'message': 'Server unavailable. Please check your connection and try again.'
};
const HTTP_STATUS = {
    OK: 200,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    INTERNAL_SERVER_ERROR: 500,
    ACCEPTED: 202,
    NO_CONTENT: 204,
    TOO_MANY_REQUESTS: 429
};

class ApiClient {
    constructor({ prefix = 'v1/' } = {}) {
        this.prefix = prefix;
    }

    async get({ url, params, token = null }) {
        return this.request({
            url,
            params,
            method: 'GET',
            token
        });
    }

    async post({ url, body = {}, token = null }) {
        return this.request({
            url,
            method: 'POST',
            body,
            token
        });
    }

    async put({ url, body = {}, params = {} }) {
        return this.request({
            url,
            method: 'PUT',
            body,
            params
        });
    }

    async patch({ url, body = {} }) {
        return this.request({
            url,
            method: 'PATCH',
            body
        });
    }

    async delete({ url, body = {} }) {
        return this.request({
            url,
            method: 'DELETE',
            body
        });
    }

    getHeaders(method, token) {
        const headers = method !== 'GET' ? { 'Content-Type': 'application/json' } : {};
        const transactionId = window.crypto?.randomUUID?.();
        const deviceId = amplitudeTracker.getDeviceId();

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        if (transactionId) {
            headers['X-Transaction-Id'] = transactionId;
        }

        if (deviceId) {
            headers['X-Device-Id'] = deviceId;
        }

        return headers;
    }

    async getJsonFromResponse(response) { /* for 201 status code */
        try {
            const json = await response.json();

            return json;
        } catch {
            return {};
        }
    }

    async request({ url, method, params = {}, body, token }) {
        const query = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
        const options = {
            method,
            headers: this.getHeaders(method, token),
            credentials: 'include',
            body: body ? JSON.stringify(body) : undefined
        };
        const fullUrl = `${config.apiUrl}${this.prefix}${url}`;
        const response = await fetch(`${fullUrl}${query}`, options);

        if (response.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) throw DEFAULT_ERROR;

        if (response.status === HTTP_STATUS.NO_CONTENT || response.status === HTTP_STATUS.ACCEPTED) return {};

        const json = await this.getJsonFromResponse(response);

        if (response.status >= HTTP_STATUS.BAD_REQUEST) {
            if (response.status === HTTP_STATUS.UNAUTHORIZED) {
                throw { message: 'Unauthorized: status - 401' }; /* eslint-disable-line */
            }

            if (response.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
                throw { message: 'Too many requests. Please try again in one minute.', code: 'TooManyRequests' }; /* eslint-disable-line */
            }

            throw json;
        }

        return json;
    }
}

export {
    ApiClient
};
